import sanitizeHtml from 'sanitize-html'

/*
 * Trusted Types support.
 *
 * The app is served with `require-trusted-types-for 'script'` in its CSP (see
 * public/index.html and security-headers.js), so every assignment to a DOM XSS
 * sink (innerHTML, script.src, ...) must go through a Trusted Types policy.
 * React 17 and the Google Maps loader assign plain strings, so we install the
 * `default` policy: the browser calls it automatically for those assignments.
 *
 * createHTML sanitizes, so anything reaching innerHTML (work order
 * descriptions, tech notes and special instructions coming from Odoo) can no
 * longer carry scripts or event handlers.
 */

// Markup that the Odoo-provided HTML and the Google Maps widgets rely on.
// Everything not listed here (script, iframe, object, on* handlers, ...) is
// dropped by sanitize-html.
const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat([
        'img', 'button', 'canvas', 'svg', 'path', 'g', 'circle', 'rect', 'line', 'polygon', 'polyline', 'label',
        // The "Sign in with Google" button is an iframe injected by the Google
        // Identity Services library; allowedIframeHostnames below keeps this
        // from opening the door to arbitrary frames in Odoo-provided HTML.
        'iframe',
    ]),
    allowedAttributes: {
        '*': ['class', 'id', 'style', 'title', 'dir', 'lang', 'role', 'tabindex', 'draggable', 'aria-*', 'data-*'],
        a: ['href', 'name', 'target', 'rel'],
        img: ['src', 'srcset', 'alt', 'width', 'height', 'loading'],
        button: ['type', 'disabled'],
        canvas: ['width', 'height'],
        iframe: ['src', 'width', 'height', 'frameborder', 'scrolling', 'allow', 'sandbox', 'allowtransparency'],
        svg: ['viewBox', 'width', 'height', 'fill', 'xmlns'],
        path: ['d', 'fill', 'stroke', 'stroke-width'],
        g: ['fill', 'stroke', 'transform'],
        circle: ['cx', 'cy', 'r', 'fill', 'stroke'],
        rect: ['x', 'y', 'width', 'height', 'fill', 'stroke'],
        line: ['x1', 'y1', 'x2', 'y2', 'stroke'],
        polygon: ['points', 'fill', 'stroke'],
        polyline: ['points', 'fill', 'stroke'],
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesByTag: { img: ['http', 'https', 'data', 'blob'] },
    // The only host any <iframe> is allowed to point at, mirroring frame-src.
    allowedIframeHostnames: ['accounts.google.com'],
    allowVulnerableTags: false,
}

// Origins allowed to be injected as <script src>, mirroring script-src in the CSP.
const ALLOWED_SCRIPT_ORIGINS = [
    'https://maps.googleapis.com/',
    'https://maps.gstatic.com/',
    'https://accounts.google.com/gsi/',
]

function isSameOrigin(url: string): boolean {
    try {
        return new URL(url, window.location.href).origin === window.location.origin;
    }
    catch (err: any) {
        return false;
    }
}

export function installTrustedTypesPolicy(): void {
    const trustedTypes = (window as any).trustedTypes;
    // Firefox and Safari do not implement Trusted Types; the CSP directive is
    // simply ignored there, so there is nothing to install.
    if (!trustedTypes || typeof trustedTypes.createPolicy !== 'function')
        return;
    try {
        trustedTypes.createPolicy('default', {
            createHTML: (html: string) => sanitizeHtml(html, SANITIZE_OPTIONS),
            createScriptURL: (url: string) => {
                if (isSameOrigin(url) || ALLOWED_SCRIPT_ORIGINS.some(origin => url.indexOf(origin) === 0))
                    return url;
                throw new Error(`Blocked script URL by Trusted Types policy: ${url}`);
            },
            // createScript is intentionally not implemented: eval() and friends
            // stay blocked, as they already are by script-src.
        });
    }
    catch (err: any) {
        // A duplicate `default` policy (StrictMode double invoke, HMR) is not fatal.
        console.log('Error', err);
    }
}
